import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Spin, message } from 'antd';
import { ArrowSquareOut, EnvelopeSimple, FileText, CheckCircle, XCircle, X } from '@phosphor-icons/react';
import contractService from '../../../../services/oneButtonServices/contractService';
import { contractApiErrorMessage, roofingProposalUrlFromApi } from './contractFlowHelpers';
import type { ViewTrinityContractRow } from './ViewTrinityContractModal';
import { OBDSelect } from './form-elements';
import DashboardModal from './DashboardModal';

import './oneButton-css/TrinityProposalModal.css';

const PROPOSAL_ERROR_MESSAGE = 'There was an issue with Proposal Generation. Please download the proposal from the portal and continue processing the Agreement, SFDC sync, and Combined Pack generation and signing through oneBUTTON';

/** Angular `ViewRoofProposalComponent.getLabelForProductType` — drives modal title and `isRoofProposal`. */
export function getAngularViewRoofProposalLabel(productTypeRaw: string | undefined): string {
    const v = String(productTypeRaw || '').trim();
    switch (v) {
        case 'RO':
        case 'R':
            return 'Roof';
        case 'BO':
        case 'B':
            return 'Battery';
        case 'S':
            return 'Solar';
        default:
            return '';
    }
}

/** Modal title: `View ${label} Proposal` — fallback when code not in Angular switch (e.g. combined codes). */
export function getViewTrinityProposalModalTitle(productTypeRaw: string | undefined): string {
    const angular = getAngularViewRoofProposalLabel(productTypeRaw);
    if (angular) return `View ${angular} Proposal`;
    const pt = String(productTypeRaw || '').toUpperCase();
    if (pt.includes('B') || pt === 'BO') return 'View Battery Proposal';
    if (pt === 'RO' || pt.includes('R')) return 'View Roof Proposal';
    return 'View Solar Proposal';
}

/** Angular `SunnovaTrinityContractModelComponent.getLabelForProductType` for `generateProposal` (roof path). */
function getGenerateProposalProductType(productTypeRaw: string | undefined): string {
    const v = String(productTypeRaw || '').trim();
    switch (v) {
        case 'RO':
            return 'Roof Only';
        case 'R':
            return 'Roof';
        default:
            return v;
    }
}

function docListFromRes(res: unknown): { downloadUrl: string; fileName: string }[] {
    if (res == null || typeof res !== 'object') return [];
    const o = res as Record<string, unknown>;
    const raw = o.url ?? o.Url;
    
    let items: unknown[] = [];
    if (Array.isArray(raw)) {
        items = raw;
    } else if (raw != null && typeof raw === 'object') {
        items = [raw];
    }

    const mapped = items
        .map((item: unknown) => {
            if (item == null || typeof item !== 'object') return null;
            const d = item as Record<string, unknown>;
            const downloadUrl = String(d.downloadUrl ?? d.DownloadUrl ?? d.url ?? d.Url ?? '');
            const fileName = String(d.fileName ?? d.FileName ?? 'Document');
            if (!downloadUrl) return null;
            return { downloadUrl, fileName };
        })
        .filter(Boolean) as { downloadUrl: string; fileName: string }[];

    if (mapped.length > 0) return mapped;

    const singleUrl = roofingProposalUrlFromApi(res);
    if (singleUrl) {
        return [{ downloadUrl: singleUrl, fileName: String(o.fileName ?? o.FileName ?? 'Proposal') }];
    }

    return [];
}

function hasDocumateUrlArray(res: Record<string, unknown> | null): boolean {
    return docListFromRes(res).length > 0;
}

function ProposalDocLink({ href, label }: { href: string; label: string }) {
    return (
        <div className="obd-tpm__doc-row" role="listitem">
            <FileText size={18} weight="duotone" className="obd-tpm__doc-row-icon" aria-hidden />
            <a href={href} target="_blank" rel="noopener noreferrer" className="obd-tpm__doc-link">
                <span className="obd-tpm__doc-text">{label}</span>
                <ArrowSquareOut size={15} weight="bold" className="obd-tpm__doc-external" aria-hidden />
            </a>
        </div>
    );
}

type Props = {
    row: ViewTrinityContractRow;
    opportunityId: string | number | null | undefined;
    onAfterAction?: () => Promise<void>;
    onClose: () => void;
    theme?: 'dark' | 'light';
    isGenerate?: boolean;
};

/**
 * Trinity `SunnovaTrinityContractModelComponent` — roof vs solar/battery templates from Angular HTML.
 */
export function TrinityProposalModal({ row, opportunityId, onAfterAction, onClose, theme = 'dark', isGenerate }: Props) {
    const angularLabel = getAngularViewRoofProposalLabel(row.productTypeCode || row.productType);
    const isRoofProposal = angularLabel === 'Roof' && !isGenerate;

    const [mergeDoc, setMergeDoc] = useState(true);
    const [contractMail, setContractMail] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState<Record<string, unknown> | null>(null);
    const [documents, setDocuments] = useState<{ downloadUrl: string; fileName: string }[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [alertModal, setAlertModal] = useState<{
        type: 'success' | 'error';
        content: string;
        onOk?: () => void;
    } | null>(null);

    const opportunityIdNum = useMemo(() => {
        if (opportunityId == null || String(opportunityId).trim() === '') return null;
        const n = Number(opportunityId);
        return Number.isFinite(n) ? n : null;
    }, [opportunityId]);

    const quoteIdNum = useMemo(() => {
        const n = Number(row.quoteIdRaw);
        return Number.isFinite(n) ? n : null;
    }, [row.quoteIdRaw]);

    const loadRoofUrl = useCallback(async () => {
        setLoading(true);
        setErrorMessage(null);
        setResponse(null);
        setDocuments([]);
        try {
            const res = await contractService.getRoofingProposalURL(row.quoteIdRaw);
            const url = roofingProposalUrlFromApi(res);
            if (url) {
                const fileName =
                    typeof res === 'object' && res !== null
                        ? String((res as Record<string, unknown>).fileName ?? (res as Record<string, unknown>).FileName ?? 'Proposal')
                        : 'Proposal';
                setResponse({
                    downloadUrl: url,
                    fileName,
                });
            } else {
                setErrorMessage('No records found');
            }
        } catch (e: unknown) {
            setErrorMessage(contractApiErrorMessage(e) ?? 'No records found');
        } finally {
            setLoading(false);
        }
    }, [row.quoteIdRaw]);

    useEffect(() => {
        if (!isRoofProposal) return;
        void loadRoofUrl();
    }, [isRoofProposal, loadRoofUrl]);

    const runRoofEmail = async () => {
        if (opportunityIdNum == null || quoteIdNum == null) {
            message.warning('Missing opportunity or quote id.');
            return;
        }
        setLoading(true);
        setErrorMessage(null);
        try {
            const pt = getGenerateProposalProductType(row.productTypeCode || row.productType);
            const res = (await contractService.generateProposal({
                opportunityId: opportunityIdNum,
                quoteID: quoteIdNum,
                productType: pt,
                manualOverride: !!row.manualOverride,
                isEmail: true,
            })) as Record<string, unknown> | null;
            const list = docListFromRes(res);
            if (list.length) setDocuments(list);
            if (res) setResponse(res);
            setAlertModal({
                type: 'success',
                content: 'Roof Proposal shared for signing.',
                onOk() {
                    onClose();
                }
            });
            await onAfterAction?.();
        } catch (e: unknown) {
            setAlertModal({
                type: 'error',
                content: contractApiErrorMessage(e) ?? PROPOSAL_ERROR_MESSAGE,
                onOk() {
                    onClose();
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const createDocument = async (isMail: boolean) => {
        const ptRaw = String(row.productTypeCode || row.productType || '');
        const isEmail = isMail ? true : contractMail === 'Email';

        if (opportunityIdNum == null) {
            message.warning('Missing opportunity id.');
            return;
        }
        if (!row.quoteIdRaw?.trim()) {
            message.warning('Missing quote id.');
            return;
        }

        setLoading(true);
        setErrorMessage(null);
        setResponse(null);
        setDocuments([]);
        try {
            if (angularLabel === 'Roof' && isGenerate) {
                const pt = getGenerateProposalProductType(row.productTypeCode || row.productType);
                const res = (await contractService.generateProposal({
                    opportunityId: opportunityIdNum,
                    quoteID: quoteIdNum,
                    productType: pt,
                    manualOverride: !!row.manualOverride,
                    isEmail,
                })) as Record<string, unknown> | null;

                const list = docListFromRes(res);
                if (list.length) setDocuments(list);
                if (res) setResponse(res);

                if (isEmail) {
                    setAlertModal({
                        type: 'success',
                        content: 'Roof Proposal shared for signing.',
                        onOk() {
                            onClose();
                        }
                    });
                } else {
                    const mailStatus = res?.MailSentStatus ?? (res as { mailSentStatus?: string })?.mailSentStatus;
                    if (mailStatus) {
                        message.success(String(mailStatus));
                    }
                }
                await onAfterAction?.();
            } else {
                const res = (await contractService.createDocumateSunnovaAPI({
                    quoteId: row.quoteIdRaw,
                    agreementType: 'Proposal',
                    isEmail,
                    mergeDocuments: mergeDoc,
                    opportunityId: opportunityIdNum,
                })) as Record<string, unknown> | null;

                const list = docListFromRes(res);
                setDocuments(list);
                setResponse(res);

                const mailStatus = res?.MailSentStatus ?? (res as { mailSentStatus?: string })?.mailSentStatus;
                if (mailStatus) {
                    message.success(String(mailStatus));
                }

                const hasUrls = res != null && hasDocumateUrlArray(res) && list.length > 0;
                if (hasUrls) {
                    await onAfterAction?.();
                }
                if (isEmail && res != null) {
                    if (hasUrls) {
                        message.success(
                            ptRaw === 'RRR'
                                ? 'Roof Proposal shared with Adobe for signing.'
                                : 'Solar Proposal shared with Adobe for signing.',
                        );
                        onClose();
                    } else {
                        setAlertModal({
                            type: 'success',
                            content: ptRaw === 'RRR' ? 'Roof Proposal shared for signing.' : 'Solar Proposal shared for signing.',
                            onOk() {
                                onClose();
                            }
                        });
                    }
                }
            }
        } catch (e: unknown) {
            const errContent = contractApiErrorMessage(e) ?? PROPOSAL_ERROR_MESSAGE;
            if (isEmail && ptRaw === 'RRR') {
                setAlertModal({
                    type: 'error',
                    content: errContent,
                    onOk() {
                        onClose();
                    }
                });
            } else {
                setAlertModal({
                    type: 'error',
                    content: errContent
                });
            }
        } finally {
            setLoading(false);
        }
    };

    if (isRoofProposal) {
        return (
            <>
            <Spin spinning={loading}>
                <div className="obd-tpm">
                    <div className="obd-tpm__doc-list" role="list">
                        {response?.downloadUrl ? (
                            <ProposalDocLink href={String(response.downloadUrl)} label={String(response.fileName ?? 'Proposal')} />
                        ) : null}
                        {documents.map((v, i) => (
                            <ProposalDocLink key={`${v.downloadUrl}-${i}`} href={v.downloadUrl} label={v.fileName} />
                        ))}
                    </div>
                    {!response?.downloadUrl && documents.length === 0 && !loading && !errorMessage ? (
                        <p className="obd-tpm__empty">No proposal link yet.</p>
                    ) : null}
                    <div className="obd-tpm__actions">
                        <Button
                            type="primary"
                            size="small"
                            className="obd-tpm__btn-primary obd-tpm__btn-primary--icon"
                            icon={<EnvelopeSimple size={16} weight="bold" aria-hidden />}
                            onClick={() => void runRoofEmail()}
                        >
                            Email Proposal
                        </Button>
                    </div>
                    {errorMessage ? <div className="obd-tpm__err">{errorMessage}</div> : null}
                </div>
            </Spin>
            {alertModal && (
                <DashboardModal
                    open
                    title=""
                    className="obd-tpm-alert-modal"
                    onClose={() => {
                        const onOk = alertModal.onOk;
                        setAlertModal(null);
                        onOk?.();
                    }}
                    width={420}
                    zIndex={12500}
                    theme={theme}
                    footer={null}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '24px 24px 18px', gap: '20px' }}>
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                            {alertModal.type === 'success' ? (
                                <CheckCircle size={28} weight="fill" style={{ color: '#22c55e', flexShrink: 0 }} />
                            ) : (
                                <XCircle size={28} weight="fill" style={{ color: '#ef4444', flexShrink: 0 }} />
                            )}
                            <div style={{ fontSize: '14px', color: theme === 'dark' ? '#f1f5f9' : '#1e293b', lineHeight: '1.5', flex: 1, paddingTop: '2px' }}>
                                {alertModal.content}
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                onClick={() => {
                                    const onOk = alertModal.onOk;
                                    setAlertModal(null);
                                    onOk?.();
                                }}
                            >
                                OK
                            </Button>
                        </div>
                    </div>
                </DashboardModal>
            )}
            </>
        );
    }

    return (
        <>
        <Spin spinning={loading}>
            <div className="obd-tpm">
                <form
                    className="obd-tpm__form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void createDocument(false);
                    }}
                >
                    <div className="obd-tpm__card obd-tpm__card--form">
                        <div className="obd-tpm__grid">
                            <div className="obd-tpm__cell obd-tpm__cell--check">
                                <Checkbox checked={mergeDoc} onChange={(e) => setMergeDoc(e.target.checked)}>
                                    Merge Document
                                </Checkbox>
                            </div>
                            <div className="obd-tpm__cell obd-tpm__cell--select">
                                <OBDSelect
                                    value={contractMail}
                                    onChange={(v) => setContractMail(v)}
                                    className="obd-tpm__select"
                                    options={[
                                        { value: '', label: 'In-Person' },
                                        { value: 'Email', label: 'Email' },
                                    ]}
                                />
                            </div>
                            <div className="obd-tpm__cell obd-tpm__cell--action">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    size="small"
                                    className="obd-tpm__btn-primary obd-tpm__btn-primary--icon"
                                    icon={
                                        contractMail === 'Email' ? (
                                            <EnvelopeSimple size={16} weight="bold" aria-hidden />
                                        ) : (
                                            <FileText size={16} weight="bold" aria-hidden />
                                        )
                                    }
                                >
                                    {contractMail === 'Email' ? 'Email Proposal' : 'View Proposal'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
                {response ? (
                    <div
                        className={`obd-tpm__results${
                            documents.length > 0 || String(response.Message ?? '').trim() !== ''
                                ? ' obd-tpm__results--split'
                                : ''
                        }`}
                    >
                        {documents.length > 0 ? (
                            <div className="obd-tpm__doc-list" role="list">
                                {documents.map((v, i) => (
                                    <ProposalDocLink key={`${v.downloadUrl}-${i}`} href={v.downloadUrl} label={v.fileName} />
                                ))}
                            </div>
                        ) : null}
                        {response.Message != null && String(response.Message).trim() !== '' ? (
                            <div className="obd-tpm__msg obd-tpm__msg--danger">{String(response.Message)}</div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </Spin>
        {alertModal && (
            <DashboardModal
                open
                title=""
                className="obd-tpm-alert-modal"
                onClose={() => {
                    const onOk = alertModal.onOk;
                    setAlertModal(null);
                    onOk?.();
                }}
                width={420}
                zIndex={12500}
                theme={theme}
                footer={null}
            >
                <div style={{ display: 'flex', flexDirection: 'column', padding: '24px 24px 18px', gap: '20px' }}>
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                        {alertModal.type === 'success' ? (
                            <CheckCircle size={28} weight="fill" style={{ color: '#22c55e', flexShrink: 0 }} />
                        ) : (
                            <XCircle size={28} weight="fill" style={{ color: '#ef4444', flexShrink: 0 }} />
                        )}
                        <div style={{ fontSize: '14px', color: theme === 'dark' ? '#f1f5f9' : '#1e293b', lineHeight: '1.5', flex: 1, paddingTop: '2px' }}>
                            {alertModal.content}
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            onClick={() => {
                                const onOk = alertModal.onOk;
                                setAlertModal(null);
                                onOk?.();
                            }}
                        >
                            OK
                        </Button>
                    </div>
                </div>
            </DashboardModal>
        )}
        </>
    );
}
